/**
 * Ticket detail drawer — full audit timeline, reply box with internal-note
 * toggle, status/category control, and the gated "Impersonate User" action.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  X, Send, EyeOff, Eye, Loader2, MessageCircle, History,
  ShieldAlert, UserCog, ArrowRight, Lock,
} from "lucide-react";
import {
  Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { supabase } from "@/lib/supabaseClient";
import { API } from "@/App";
import { cn } from "@/lib/utils";
import { ImpersonationStore } from "@/lib/impersonation";
import { friendlyError } from "@/lib/friendlyError";

const STATUSES = ["open", "in_progress", "awaiting_chef", "resolved", "closed"];

function EventRow({ ev }) {
  const isReply = ev.event_type === "reply";
  const isInternal = ev.is_internal;
  const isImpersonation = ev.event_type === "impersonation_started" || ev.event_type === "impersonation_ended";
  const isChef = ev.actor_role === "chef";

  let label = ev.event_type.replace(/_/g, " ");
  if (ev.event_type === "status_change") label = `status: ${ev.old_value} → ${ev.new_value}`;
  if (ev.event_type === "category_change") label = `category: ${ev.old_value} → ${ev.new_value}`;
  if (ev.event_type === "assignment_change") label = `assigned: ${ev.old_value?.slice(0,8) || "unassigned"} → ${ev.new_value?.slice(0,8) || "unassigned"}`;

  return (
    <div
      className={cn(
        "rounded-lg p-3 border",
        isInternal && "border-violet-500/30 bg-violet-500/5",
        !isInternal && isReply && !isChef && "border-emerald-500/30 bg-emerald-500/5",
        !isInternal && isReply && isChef && "border-amber-500/30 bg-amber-500/5",
        !isReply && !isImpersonation && !isInternal && "border-slate-800 bg-slate-900/50",
        isImpersonation && "border-rose-500/40 bg-rose-500/5",
      )}
      data-testid={`hq-ticket-event-${ev.event_type}`}
    >
      <div className="flex items-center justify-between gap-2 mb-1.5">
        <div className="flex items-center gap-1.5 text-[0.65rem]">
          {isInternal && (
            <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-violet-500/20 text-violet-300 font-semibold">
              <Lock className="h-2.5 w-2.5" />INTERNAL
            </span>
          )}
          {isImpersonation && (
            <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-rose-500/20 text-rose-300 font-semibold">
              <ShieldAlert className="h-2.5 w-2.5" />{ev.event_type === "impersonation_started" ? "IMPERSONATION" : "EXIT"}
            </span>
          )}
          <span className="text-slate-400 font-mono">
            {ev.actor_email || ev.actor_full_name || ev.actor_id?.slice(0, 8)}
          </span>
          <span className="text-slate-600 text-[0.6rem] uppercase tracking-wider">
            {ev.actor_role}
          </span>
        </div>
        <span className="text-[0.6rem] text-slate-600 font-mono whitespace-nowrap">
          {new Date(ev.created_at).toLocaleString()}
        </span>
      </div>
      {isReply ? (
        <p className="text-xs text-slate-200 whitespace-pre-wrap leading-relaxed">{ev.body}</p>
      ) : (
        <p className="text-[0.7rem] text-slate-400 italic">{label}{ev.body ? ` — ${ev.body}` : ""}</p>
      )}
    </div>
  );
}

export default function TicketDetailDrawer({ ticketId, me, onClose }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [reply, setReply] = useState("");
  const [isInternal, setIsInternal] = useState(false);
  const [busy, setBusy] = useState(false);
  const [impersonating, setImpersonating] = useState(false);

  const canWrite = me?.role === "root" || me?.role === "staff_edit";

  const authHeaders = useCallback(async () => {
    const { data: { session } } = await supabase.auth.getSession();
    return { Authorization: `Bearer ${session?.access_token || ""}` };
  }, []);

  const fetchTicket = useCallback(async () => {
    if (!ticketId) return;
    setLoading(true);
    try {
      const h = await authHeaders();
      const res = await axios.get(`${API}/admin/tickets/${ticketId}`, { headers: h });
      setData(res.data);
    } catch (err) {
      toast.error(friendlyError(err, "Failed to load ticket"));
    } finally {
      setLoading(false);
    }
  }, [ticketId, authHeaders]);

  useEffect(() => { if (ticketId) fetchTicket(); }, [ticketId, fetchTicket]);

  const sendReply = async () => {
    if (!reply.trim()) return;
    setBusy(true);
    try {
      const h = await authHeaders();
      await axios.post(`${API}/admin/tickets/${ticketId}/reply`,
        { body: reply.trim(), is_internal: isInternal },
        { headers: h }
      );
      toast.success(isInternal ? "Internal note saved" : "Reply sent to chef");
      setReply(""); setIsInternal(false);
      fetchTicket();
    } catch (err) {
      toast.error(friendlyError(err, "Reply failed. Please try again."));
    } finally {
      setBusy(false);
    }
  };

  const changeStatus = async (newStatus) => {
    setBusy(true);
    try {
      const h = await authHeaders();
      await axios.patch(`${API}/admin/tickets/${ticketId}`, { status: newStatus }, { headers: h });
      toast.success(`Status → ${newStatus}`);
      fetchTicket();
    } catch (err) {
      toast.error(friendlyError(err, "Status update failed. Please try again."));
    } finally {
      setBusy(false);
    }
  };

  const startImpersonation = async () => {
    if (!canWrite) return;
    if (!window.confirm(
      "Start READ-ONLY impersonation? You'll see PlateProfit as this chef does, but any write action will be blocked. The session is fully audited."
    )) return;
    setImpersonating(true);
    try {
      const h = await authHeaders();
      const res = await axios.post(`${API}/admin/tickets/${ticketId}/impersonate`, {
        reason: "Diagnosing ticket " + data?.ticket?.reference,
      }, { headers: h });
      ImpersonationStore.write({
        access_token: res.data.access_token,
        chef_id: res.data.chef_id,
        chef_email: res.data.chef_email,
        ticket_id: res.data.ticket_id,
        ticket_reference: res.data.ticket_reference,
        expires_at: res.data.expires_at,
        issued_at: Math.floor(Date.now() / 1000),
      });
      toast.success("Impersonation started — read-only mode");
      navigate("/recipes");
    } catch (err) {
      toast.error(friendlyError(err, "Couldn't start impersonation. Please try again."));
    } finally {
      setImpersonating(false);
    }
  };

  const ticket = data?.ticket;
  const events = data?.events || [];

  return (
    <Sheet open={!!ticketId} onOpenChange={(o) => !o && onClose?.()}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-2xl bg-slate-950 border-l border-emerald-500/20 overflow-y-auto"
        data-testid="hq-ticket-drawer"
      >
        {loading || !ticket ? (
          <div className="py-20 text-center text-slate-500">
            <Loader2 className="h-5 w-5 animate-spin inline" />
          </div>
        ) : (
          <>
            <SheetHeader>
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <SheetTitle className="text-white text-base flex items-center gap-2">
                    <span className="font-mono text-amber-300" data-testid="hq-ticket-drawer-reference">{ticket.reference}</span>
                    <span className="opacity-50">·</span>
                    <span className="font-normal">{ticket.subject}</span>
                  </SheetTitle>
                  <SheetDescription className="text-slate-400 text-xs">
                    From <span className="font-mono">{ticket.chef_email || ticket.user_id?.slice(0,8)}</span>
                    · {new Date(ticket.created_at).toLocaleString()}
                    {ticket.hours_open != null && (
                      <span className={cn(
                        "ml-2 px-1.5 py-0.5 rounded text-[0.6rem] font-bold",
                        ticket.aging_band === "overdue" && "bg-rose-500 text-white",
                        ticket.aging_band === "approaching" && "bg-amber-500 text-slate-950",
                        ticket.aging_band === "fresh" && "bg-emerald-500/20 text-emerald-300",
                      )}>
                        {Math.round(ticket.hours_open)}h
                      </span>
                    )}
                  </SheetDescription>
                </div>
              </div>
            </SheetHeader>

            {/* Status pills */}
            <div className="mt-4 flex flex-wrap gap-1.5" data-testid="hq-ticket-drawer-status-pills">
              {STATUSES.map((s) => (
                <button
                  key={s} type="button"
                  disabled={!canWrite || busy || ticket.status === s}
                  onClick={() => changeStatus(s)}
                  className={cn(
                    "px-2.5 py-1 rounded text-[0.65rem] font-semibold border transition-colors",
                    ticket.status === s
                      ? "bg-emerald-500 text-slate-950 border-emerald-400"
                      : canWrite
                        ? "bg-slate-900 text-slate-400 border-slate-700 hover:border-emerald-500/50"
                        : "bg-slate-900 text-slate-600 border-slate-800 cursor-not-allowed"
                  )}
                  data-testid={`hq-ticket-status-${s}`}
                >
                  {s.replace(/_/g, " ")}
                </button>
              ))}
            </div>

            {/* Impersonate button (root + staff_edit only) */}
            {canWrite && (
              <div className="mt-4 p-3 rounded-lg border border-rose-500/30 bg-rose-500/5">
                <div className="flex items-start gap-3">
                  <ShieldAlert className="h-4 w-4 text-rose-400 flex-none mt-0.5" />
                  <div className="flex-1 text-xs">
                    <div className="font-semibold text-rose-200 mb-1">Read-only impersonation</div>
                    <p className="text-slate-400 leading-relaxed">
                      See PlateProfit through this chef&apos;s eyes to diagnose UI issues.
                      Write actions are blocked server-side. Fully audited.
                    </p>
                  </div>
                  <Button
                    size="sm" onClick={startImpersonation} disabled={impersonating}
                    className="bg-rose-600 hover:bg-rose-500 text-white font-semibold"
                    data-testid="hq-ticket-impersonate-btn"
                  >
                    {impersonating ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <UserCog className="h-3 w-3 mr-1" />}
                    Impersonate
                    <ArrowRight className="h-3 w-3 ml-1" />
                  </Button>
                </div>
              </div>
            )}

            {/* Original message */}
            <div className="mt-6">
              <div className="text-[0.65rem] uppercase tracking-[0.2em] text-slate-500 font-mono mb-2 flex items-center gap-2">
                <MessageCircle className="h-3 w-3" />Original message
              </div>
              <div className="rounded-lg border border-amber-500/20 bg-amber-500/5 p-3">
                <p className="text-xs text-slate-200 whitespace-pre-wrap leading-relaxed">{ticket.message}</p>
                {ticket.rating && (
                  <div className="mt-2 inline-flex items-center gap-1 text-amber-400 text-xs">
                    Rating: {"★".repeat(ticket.rating)}{"☆".repeat(5 - ticket.rating)}
                  </div>
                )}
              </div>
            </div>

            {/* Timeline */}
            <div className="mt-6">
              <div className="text-[0.65rem] uppercase tracking-[0.2em] text-slate-500 font-mono mb-2 flex items-center gap-2">
                <History className="h-3 w-3" />Timeline ({events.length})
              </div>
              <div className="space-y-2" data-testid="hq-ticket-events">
                {events.length === 0 ? (
                  <p className="text-xs text-slate-600 italic">No activity yet.</p>
                ) : events.map((ev) => <EventRow key={ev.id} ev={ev} />)}
              </div>
            </div>

            {/* Reply box */}
            {canWrite ? (
              <div className="mt-6 sticky bottom-0 -mx-6 px-6 py-4 bg-slate-950 border-t border-slate-800">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[0.65rem] uppercase tracking-[0.2em] text-slate-500 font-mono">
                    {isInternal ? "Internal note (chef can NOT see)" : "Reply to chef"}
                  </div>
                  <button
                    type="button"
                    onClick={() => setIsInternal((v) => !v)}
                    className={cn(
                      "inline-flex items-center gap-1 text-[0.65rem] px-2 py-1 rounded border transition-colors",
                      isInternal
                        ? "bg-violet-500/15 text-violet-300 border-violet-500/40"
                        : "bg-slate-900 text-slate-400 border-slate-700 hover:border-violet-500/50"
                    )}
                    data-testid="hq-ticket-toggle-internal"
                  >
                    {isInternal ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
                    {isInternal ? "Internal" : "Public"}
                  </button>
                </div>
                <Textarea
                  value={reply} onChange={(e) => setReply(e.target.value)}
                  placeholder={isInternal ? "Notes only your team will see…" : "Reply visible to the chef…"}
                  rows={3}
                  className={cn(
                    "border-slate-700 text-white",
                    isInternal ? "bg-violet-500/5 border-violet-500/30" : "bg-slate-900"
                  )}
                  data-testid="hq-ticket-reply-textarea"
                />
                <div className="mt-2 flex items-center justify-end">
                  <Button
                    size="sm" onClick={sendReply} disabled={busy || !reply.trim()}
                    className={cn(
                      "font-semibold",
                      isInternal
                        ? "bg-violet-500 hover:bg-violet-400 text-white"
                        : "bg-emerald-500 hover:bg-emerald-400 text-slate-950"
                    )}
                    data-testid="hq-ticket-reply-submit"
                  >
                    {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Send className="h-3.5 w-3.5 mr-1" />}
                    {isInternal ? "Save note" : "Send reply"}
                  </Button>
                </div>
              </div>
            ) : (
              <div className="mt-6 text-[0.7rem] text-slate-500 italic text-center py-2">
                Read-only role — replies + impersonation are limited to staff_edit and root.
              </div>
            )}
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
